/**
 * SearchResults Page
 * Displays search results with filters and pagination
 */
import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Chip,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Divider,
  Breadcrumbs,
  Link as MuiLink
} from '@mui/material';
import {
  Storage,
  Shield,
  Description,
  Person,
  Search as SearchIcon,
  Storage as StorageIcon
} from '@mui/icons-material';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { globalSearch, SearchResults as SearchResultsType } from '../services/searchService';

const SearchResults: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const query = searchParams.get('q') || '';
  
  const [results, setResults] = useState<SearchResultsType | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0);

  // Fetch search results
  useEffect(() => {
    if (query) {
      performSearch();
    }
  }, [query]);

  const performSearch = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const searchResults = await globalSearch({ query, limit: 20 });
      setResults(searchResults);
    } catch (err: any) {
      setError(err.message || 'Search failed');
    } finally {
      setLoading(false);
    }
  };

  // Get icon for content type
  const getIcon = (contentType: string) => {
    switch (contentType) {
      case 'dataAsset':
        return <Storage sx={{ color: '#003366' }} />;
      case 'policy':
        return <Shield sx={{ color: '#003366' }} />;
      case 'documentation':
        return <Description sx={{ color: '#003366' }} />;
      case 'user':
        return <Person sx={{ color: '#003366' }} />;
      default:
        return <SearchIcon sx={{ color: '#003366' }} />;
    }
  };

  // Get result link
  const getResultLink = (result: any) => {
    switch (result.contentType) {
      case 'dataAsset':
        return `/data-catalog/${result._id}`;
      case 'policy':
        return `/policies/${result._id}`;
      case 'documentation':
        return `/program-documentation/${result.projectId}`;
      case 'user':
        return `/users/${result._id}`;
      default:
        return '#';
    }
  };

  // Get result title
  const getResultTitle = (result: any) => {
    return result.name || result.projectName || result.title || 'Untitled';
  };

  // Get result description
  const getResultDescription = (result: any) => {
    return result.description || result.email || result.category || '';
  };

  // Tab change handler
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  // Get filtered results based on active tab
  const getFilteredResults = () => {
    if (!results) return [];
    
    switch (activeTab) {
      case 0: // All
        return [
          ...results.results.dataAssets,
          ...results.results.policies,
          ...results.results.documentation,
          ...results.results.users
        ];
      case 1: // Data Assets
        return results.results.dataAssets;
      case 2: // Policies
        return results.results.policies;
      case 3: // Documentation
        return results.results.documentation;
      case 4: // Users
        return results.results.users;
      default:
        return [];
    }
  };

  const filteredResults = getFilteredResults();

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Breadcrumbs - Unidatum Style */}
      <Breadcrumbs 
        aria-label="breadcrumb" 
        sx={{ 
          mb: 3,
          '& .MuiBreadcrumbs-separator': {
            color: '#71767A'
          }
        }}
      >
        <MuiLink 
          component={Link} 
          to="/" 
          sx={{ 
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            color: '#0066CC',
            textDecoration: 'none',
            fontSize: '14px',
            fontWeight: 500,
            '&:hover': {
              textDecoration: 'underline'
            }
          }}
        >
          <StorageIcon sx={{ fontSize: 18 }} />
          E-UNIFY
        </MuiLink>
        <Typography 
          sx={{ 
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            color: '#71767A',
            fontSize: '14px',
            fontWeight: 500
          }}
        >
          <SearchIcon sx={{ fontSize: 18 }} />
          SEARCH_RESULTS
        </Typography>
      </Breadcrumbs>

      {/* Search Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ color: '#003366', fontWeight: 600 }}>
          Search Results
        </Typography>
        {query && (
          <Typography variant="body1" color="text.secondary">
            Showing results for: <strong>"{query}"</strong>
          </Typography>
        )}
        {results && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Found {results.totalResults} results in {results.searchTime}ms
          </Typography>
        )}
      </Box>

      {/* Loading State */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress sx={{ color: '#003366' }} />
        </Box>
      )}

      {/* Error State */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Results */}
      {!loading && results && (
        <>
          {/* Tabs for filtering */}
          <Paper sx={{ mb: 3 }}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                borderBottom: 1,
                borderColor: 'divider',
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 500,
                },
                '& .Mui-selected': {
                  color: '#003366',
                },
              }}
            >
              <Tab label={`All (${results.totalResults})`} />
              <Tab label={`Data Assets (${results.results.dataAssets.length})`} />
              <Tab label={`Policies (${results.results.policies.length})`} />
              <Tab label={`Documentation (${results.results.documentation.length})`} />
              <Tab label={`Users (${results.results.users.length})`} />
            </Tabs>
          </Paper>

          {/* Results Grid */}
          {filteredResults.length > 0 ? (
            <Grid container spacing={2}>
              {filteredResults.map((result: any) => (
                <Grid item xs={12} key={result._id}>
                  <Card
                    sx={{
                      '&:hover': {
                        boxShadow: 3,
                        borderColor: '#003366',
                      },
                      border: '1px solid #e0e0e0',
                      transition: 'all 0.2s',
                    }}
                  >
                    <CardActionArea
                      component={Link}
                      to={getResultLink(result)}
                      sx={{ p: 2 }}
                    >
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <Box sx={{ flexShrink: 0, pt: 0.5 }}>
                          {getIcon(result.contentType)}
                        </Box>
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography
                            variant="h6"
                            component="h2"
                            sx={{
                              color: '#003366',
                              fontWeight: 600,
                              mb: 0.5,
                              '&:hover': {
                                textDecoration: 'underline',
                              },
                            }}
                          >
                            {getResultTitle(result)}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {getResultDescription(result)}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                            <Chip
                              label={result.contentType}
                              size="small"
                              sx={{
                                backgroundColor: '#f0f5fb',
                                color: '#003366',
                                fontWeight: 500,
                              }}
                            />
                            {result.status && (
                              <Chip label={result.status} size="small" variant="outlined" />
                            )}
                            {result.type && (
                              <Chip label={result.type} size="small" variant="outlined" />
                            )}
                            {result.role && (
                              <Chip label={result.role} size="small" variant="outlined" />
                            )}
                          </Box>
                        </Box>
                      </Box>
                    </CardActionArea>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <SearchIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No results found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Try adjusting your search terms or filters
              </Typography>
            </Paper>
          )}
        </>
      )}

      {/* Empty State - No Query */}
      {!loading && !query && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <SearchIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Enter a search query
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Use the search bar above to find data assets, policies, documentation, and users
          </Typography>
        </Paper>
      )}
    </Container>
  );
};

export default SearchResults;
